import { api } from './client';
import type { ApiSuccess, PublicUser } from '@/types/api';

export interface TwoFactorStatus {
  enabled: boolean;
  enabledAt: string | null;
  /** Setup was started but never confirmed — sign-in is still single-step. */
  pending: boolean;
  unusedRecoveryCodes: number;
  requiredByPolicy: boolean;
}

export interface TwoFactorSetup {
  secret: string;
  otpauthUri: string;
}

export interface SecuritySettings {
  id: string;
  require2faForAdmins: boolean;
  require2faForSupport: boolean;
  updatedAt: string;
}

/** What `/auth/login` answers: either a session, or a pending challenge. */
export type LoginOutcome =
  | { requiresTwoFactor: true; challengeToken: string }
  | {
      requiresTwoFactor: false;
      accessToken: string;
      user: PublicUser;
      twoFactorEnrolmentRequired?: boolean;
      usedRecoveryCode?: boolean;
    };

export const twoFactorApi = {
  async status() {
    const res = await api.get<ApiSuccess<TwoFactorStatus>>('/auth/2fa/status');
    return res.data.data;
  },

  /** Step 1. Nothing about signing in changes until `enable` succeeds. */
  async setup() {
    const res = await api.post<ApiSuccess<TwoFactorSetup>>('/auth/2fa/setup', {});
    return res.data.data;
  },

  /** Step 2. Returns the recovery codes — shown once, never retrievable. */
  async enable(code: string) {
    const res = await api.post<ApiSuccess<{ recoveryCodes: string[] }>>(
      '/auth/2fa/enable',
      { code },
    );
    return res.data.data;
  },

  async disable(password: string, code: string) {
    const res = await api.post<ApiSuccess<{ enabled: boolean }>>('/auth/2fa/disable', {
      password,
      code,
    });
    return res.data.data;
  },

  async regenerateRecoveryCodes(code: string) {
    const res = await api.post<ApiSuccess<{ recoveryCodes: string[] }>>(
      '/auth/2fa/recovery-codes',
      { code },
    );
    return res.data.data;
  },

  /** Completes a sign-in that returned a challenge. */
  async verify(challengeToken: string, code: string) {
    const res = await api.post<ApiSuccess<LoginOutcome>>('/auth/2fa/verify', {
      challengeToken,
      code,
    });
    return res.data.data;
  },

  async securitySettings() {
    const res = await api.get<ApiSuccess<SecuritySettings>>('/auth/security-settings');
    return res.data.data;
  },

  async updateSecuritySettings(patch: {
    require2faForAdmins?: boolean;
    require2faForSupport?: boolean;
  }) {
    const res = await api.patch<ApiSuccess<SecuritySettings>>(
      '/auth/security-settings',
      patch,
    );
    return res.data.data;
  },
};
